import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { useAuthActions } from "@convex-dev/auth/react";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery, useConvexAuth } from "convex/react";
import { api } from "@/convex/_generated/api";
import AuthLoading from "@/components/auth/auth-loading";
import { navigateBasedOnRole } from "@/lib/auth/role-redirect";
import { getSecureSignInErrorMessage } from "@/lib/errors/auth-errors";
import { useToastAction } from "@/hooks/useToastAction";
import {
  AuthLeftPanel,
  AuthRightPanel,
  AuthMobileLogo,
} from "@/components/auth/auth-layout";
import { GoogleAuthButton } from "@/components/auth/google-auth-button";
import { isLocalInstallEdition } from "@/lib/edition/edition";
import { isDesktopApp } from "@/lib/edition/is-desktop";
import { openExternal } from "@/lib/desktop/open-external";

// URL pública del ERP web. Espejo en cliente de `SITE_URL` (server usa
// `process.env.SITE_URL || "https://erp.heskala.com"`): no la quemamos, sale de
// `VITE_SITE_URL` con el mismo fallback. En desktop el registro se hace en la
// web (el webview es login-only), así que la abrimos en el navegador del sistema.
const SITE_URL = (
  (import.meta.env.VITE_SITE_URL as string | undefined) ||
  "https://erp.heskala.com"
).replace(/\/$/, "");
const SIGN_UP_URL = `${SITE_URL}/auth/sign-up`;

const getSignInErrorMessage = (error: any): string => {
  return getSecureSignInErrorMessage(error);
};

const signInSearchSchema = z.object({
  message: z.string().optional(),
});

export const Route = createFileRoute("/_public/auth/sign-in")({
  validateSearch: signInSearchSchema,
  component: SignInPage,
});

function SignInPage() {
  const { signIn, signOut } = useAuthActions();
  const navigate = useNavigate();
  const { message } = Route.useSearch();
  const currentUser = useQuery(api.system.users.queries.getCurrentUser);
  const { isAuthenticated, isLoading: authLoading } = useConvexAuth();
  // On-prem sin usuarios: no hay cuenta para iniciar sesión, hay que crear el
  // primer admin. En cloud siempre es false.
  const bootstrapAvailable = useQuery(
    api.system.onboarding.queries.isBootstrapAvailable,
  );
  // ¿El reset por email (OTP) puede funcionar? En on-prem sin RESEND es false:
  // el flujo OTP nunca entregaría el código, así que en vez del link mostramos
  // la vía real (pedir un link de acceso al administrador).
  const passwordResetEmailEnabled = useQuery(
    api.system.onboarding.queries.isPasswordResetEmailEnabled,
  );

  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (message === "account-configured") {
      toast.success(
        "Cuenta configurada exitosamente. Ahora puedes iniciar sesión.",
      );
    } else if (message === "password-reset") {
      toast.success(
        "Contraseña restablecida exitosamente. Inicia sesión con tu nueva contraseña.",
      );
    }
  }, [message]);

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const loginAction = async (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);
    formData.append("flow", "signIn");

    const signedIn = await signIn("password", formData);
    if (!signedIn) {
      await signOut();
      throw new Error("InvalidCredentials");
    }

    // El registro de la sesión persistida lo hace el shell autenticado
    // (AuthGuardCombined en app/routes/_authenticated.tsx) al detectar
    // isAuthenticated=true. Mismo patron que mobile: este componente se
    // desmonta en cuanto navega el useEffect de abajo, y dejar la mutacion
    // aqui competia con el redirect (la promise quedaba colgada, no siempre
    // llegaba al backend, y persistCurrentSessionId no se ejecutaba). Ver
    // commit d85efb96 para el equivalente mobile.
  };

  const { execute: login, isLoading: isLoginLoading } = useToastAction(
    loginAction,
    {
      loadingMessage: "Iniciando sesión...",
      successMessage: undefined,
      errorMessage: getSignInErrorMessage,
    },
  );

  const handleLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await login(e);
  };

  useEffect(() => {
    // PRIORIDAD: primer arranque on-prem (DB sin usuarios) manda a /setup,
    // aunque exista una sesión previa (stale) que marque isAuthenticated. Sin
    // este orden, el redirect a /dashboard (abajo) corría después y ganaba, y
    // el setup se saltaba yendo directo al dashboard.
    if (bootstrapAvailable === true) {
      navigateRef.current({ to: "/setup", replace: true });
      return;
    }

    // Solo redirigir al dashboard si la sesión está REALMENTE activa. Chequear
    // solo `currentUser` causaba un bucle al cerrar sesión: tras signOut,
    // `isAuthenticated` pasa a false pero `currentUser` queda stale (truthy)
    // unos ms → este efecto navegaba a /dashboard → AuthGuard veía
    // isAuthenticated=false → /sign-in → ... ping-pong infinito.
    if (isAuthenticated && currentUser) {
      navigateBasedOnRole(currentUser, navigateRef.current);
    }
  }, [bootstrapAvailable, isAuthenticated, currentUser]);

  // Evita el flash del formulario al reabrir la app: mientras auth resuelve,
  // si ya está autenticado (los efectos de arriba redirigen al dashboard según
  // rol), o mientras se decide el bootstrap on-prem, mostramos un loader en vez
  // del form.
  if (authLoading || isAuthenticated || bootstrapAvailable === undefined) {
    return <AuthLoading title="Heskala ERP" subtitle="Cargando…" />;
  }

  return (
    <div className="relative min-h-screen">
      <AuthLeftPanel
        headline=""
        subtitle="Entra a tu empresa: tus colecciones, flujos, scripts y paneles, con tus permisos y desde cualquier lugar."
        badge=""
      />

      <AuthRightPanel>
        <div className="mb-8 text-center lg:text-left">
          <AuthMobileLogo />
          <h2 className="text-2xl font-bold tracking-tight text-foreground">
            Bienvenido de vuelta
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            Ingresa tus credenciales para acceder a tu cuenta
          </p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Correo electrónico</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="tu@email.com"
              required
              disabled={isLoginLoading}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <Label htmlFor="password">Contraseña</Label>
              {passwordResetEmailEnabled === false ? (
                // Sin email en el backend el OTP no llegaría. En vez de un texto
                // largo, dejamos el cue "¿Olvidaste tu contraseña?" (muted, no es
                // link porque no hay flujo) + un icono de info cuyo tooltip
                // explica la vía real (link de acceso del admin).
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  ¿Olvidaste tu contraseña?
                  <Tooltip>
                    <TooltipTrigger
                      type="button"
                      tabIndex={-1}
                      className="inline-flex cursor-help"
                      aria-label="Cómo recuperar el acceso"
                    >
                      <Info className="h-3.5 w-3.5" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      Este servidor no tiene correo configurado. Pedile a tu
                      administrador un link de acceso: lo genera desde el panel de
                      usuarios y con él definís una contraseña nueva.
                    </TooltipContent>
                  </Tooltip>
                </span>
              ) : (
                <Link
                  to="/auth/reset-password"
                  className="text-xs text-primary hover:underline"
                >
                  ¿Olvidaste tu contraseña?
                </Link>
              )}
            </div>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="********"
                required
                disabled={isLoginLoading}
              />
              <button
                type="button"
                className="absolute border-none right-0 bg-transparent top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoginLoading}
              >
                {showPassword ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </button>
            </div>
          </div>

          <Button type="submit" className="w-full" disabled={isLoginLoading}>
            {isLoginLoading ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Iniciando sesión...
              </>
            ) : (
              <>
                Iniciar sesión
                <ArrowRight className="ml-2 size-4" />
              </>
            )}
          </Button>
        </form>

        {/* Gap visual entre el boton de password y los botones alternativos
            (OTP, Google). Sin el divider con la "o" (que quitamos por peticion
            del diseno), el ojo necesita este respiro para separar los grupos. */}
        <div className="h-2" aria-hidden="true" />

        {/* OTP al correo: arriba de Google. No necesita contraseña; útil para
            agentes IA que reciben el código en una bandeja AgentMail, y para
            usuarios que prefieren no tipear contraseña. Requiere RESEND_API_KEY
            en el backend; igual que el resto de flujos OTP, se oculta en on-prem
            sin email. */}
        {passwordResetEmailEnabled !== false && (
          <Button
            type="button"
            variant="outline"
            className="w-full"
            onClick={() => navigate({ to: "/auth/sign-in-with-otp" })}
            disabled={isLoginLoading}
          >
            Iniciar sesión con código (OTP)
          </Button>
        )}

        <GoogleAuthButton disabled={isLoginLoading} />

        <div className="mt-6 space-y-3 text-center text-sm">
          {/* Registro para la edición CLOUD (web o desktop). Las instalaciones
              locales (on-prem / free_desktop) no tienen registro: las cuentas
              las crea el admin. En desktop abrimos el registro de la web en el
              navegador del sistema; en web es un link in-app normal. */}
          {!isLocalInstallEdition() ? (
            <div className="text-muted-foreground">
              ¿No tienes cuenta?{" "}
              {isDesktopApp() ? (
                <a
                  href={SIGN_UP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => {
                    // En el webview de Tauri un <a target="_blank"> no abre
                    // nada; delegamos al navegador del sistema.
                    e.preventDefault();
                    void openExternal(SIGN_UP_URL);
                  }}
                  className="text-primary hover:underline font-medium"
                >
                  Crea tu cuenta gratis
                </a>
              ) : (
                <Link
                  to="/auth/sign-up"
                  className="text-primary hover:underline font-medium"
                >
                  Crea tu cuenta gratis
                </Link>
              )}
            </div>
          ) : null}
          {/* "Configura tu cuenta" usa el OTP por email (mismo flujo que el
              reset). Sin email en el backend (on-prem) no funcionaría: ahí la
              primera configuración se hace con el link de activación que genera
              el admin, así que ocultamos esta opción. */}
          {passwordResetEmailEnabled !== false && (
            <div className="text-muted-foreground">
              ¿Primera vez?{" "}
              <Link
                to="/auth/first-time-login"
                className="text-primary hover:underline font-medium"
              >
                Configura tu cuenta
              </Link>
            </div>
          )}
        </div>
      </AuthRightPanel>
    </div>
  );
}
